import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { isUserLoggedIn, getUserRole } from '../../shared/utils/function';
import { ROLE, DEFAULT_USER_AVATAR } from '../../shared/constants/common';
import { CREDITS_DISPLAY_TEXT } from '../../shared/constants/message';
import { BackArrowIcon } from '../../assets/Icons/SVG';
import { useNavigation } from '../../shared/utils/useNavigation';
import { fetchUser } from '../../shared/actions/userActions';
import { fetchCreator } from '../../shared/actions/creatorActions';
import { fetchWalletBalanceAction } from '../../shared/actions/creditAction';

const MessageHeader = ({
  imgSrc,
  profileUsername = null,
  title,
  subtitle,
  showPriority = false,
  showWhaleSubscriber = false,
  showCredits = false,
  creditsText = CREDITS_DISPLAY_TEXT,
  onCreditsClick,
  showNewChatButton = false,
  showBackButton = false,
  onBackClick,
  showAtPrefixOnTitle = true,
  className = ''
}) => {
  const isLoggedIn = isUserLoggedIn();
  const role = Number(getUserRole());
  const isCreator = role === ROLE.CREATOR_SUB || role === ROLE.CREATOR_CONTENT;
  const shouldFetchWalletBalance = role === ROLE.USER;
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const history = useHistory();
  const user = useSelector((state) => state.user?.user);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(fetchUser());
      if (shouldFetchWalletBalance) {
        dispatch(fetchWalletBalanceAction());
      }
    }
  }, [isLoggedIn, shouldFetchWalletBalance, dispatch]);

  useEffect(() => {
    if (isCreator && user?.username) {
      dispatch(fetchCreator(user.username));
    }
  }, [user, isCreator, dispatch]);

  const handleBackNavigation = useCallback(() => {
    if (typeof onBackClick === 'function') {
      onBackClick();
      return;
    }
    if (history?.length > 1) {
      navigate.back();
    } else {
      navigate.toDiscover();
    }
  }, [history, navigate, onBackClick]);

  const handleNewChatClick = useCallback(() => {
    navigate.toNewMessage();
  }, [navigate]);

  return (
    <header id="cm_chat_header" className={`cm_header header_bottom_border message_page_header in_chat_header ${className} ${!showBackButton ? 'cm_no_back_btn' : ''}`}>
      <div className="w-full d-flex align-items-center gap-3">
        {showBackButton && (
          <button
            type="button"
            className="modern-back-btn"
            onClick={handleBackNavigation}
            aria-label="Go back"
          >
            <BackArrowIcon />
          </button>
        )}
        <div className="own_avtor">
          <img src={imgSrc || DEFAULT_USER_AVATAR} alt="" />
        </div>
        <div>
          {title && (
            <h1 className="message_header_title_row">
              {profileUsername ? (
                <button
                  type="button"
                  className="message_header_username_link"
                  onClick={() => navigate.toProfile(profileUsername)}
                  aria-label={`Go to @${profileUsername} profile`}
                >
                  {profileUsername !== 'Loading...' ? `@${profileUsername}` : profileUsername}
                </button>
              ) : (
                showAtPrefixOnTitle ? (title && `@${title}`) : title
              )}
              {showPriority && (
                <span className="message_header_priority_badge">
                  <span className="priority_indicator" aria-hidden="true" />
                  <span>Priority</span>
                </span>
              )}
              {showWhaleSubscriber && (
                <span aria-label="Whale subscriber"> 🐋</span>
              )}
            </h1>
          )}

          {subtitle && (
            <p
              className={
                showPriority
                  ? 'message_header_subtitle_line message_header_subtitle_line_below_priority'
                  : 'message_header_subtitle_line'
              }
            >
              {subtitle}
            </p>
          )}
        </div>

        {showCredits && (
          <span
            className="credits_plus_chip ml-auto"
            role="button"
            tabIndex={0}
            onClick={onCreditsClick}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && onCreditsClick) {
                e.preventDefault();
                onCreditsClick();
              }
            }}
            aria-label="Purchase credits"
          >
            {creditsText}
          </span>
        )}

        {showNewChatButton && (
          <div className="ml-auto">
            <span
              className="new_chat_btn"
              onClick={handleNewChatClick}
            >
              <i className="fa-solid fa-pen-to-square"></i>
            </span>
          </div>
        )}
      </div>
    </header>
  );
};

MessageHeader.propTypes = {
  imgSrc: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  showPriority: PropTypes.bool,
  showWhaleSubscriber: PropTypes.bool,
  showCredits: PropTypes.bool,
  creditsText: PropTypes.string,
  onCreditsClick: PropTypes.func,
  showNewChatButton: PropTypes.bool,
  showBackButton: PropTypes.bool,
  onBackClick: PropTypes.func,
  showAtPrefixOnTitle: PropTypes.bool,
  className: PropTypes.string,
};

export default MessageHeader;

